import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiShoppingBag,
  FiLayers,
  FiUser,
  FiGift,
  FiDatabase,
  FiClock,
  FiList,
  FiBookOpen,
  FiShield
} from 'react-icons/fi';
import { accountAPI } from '../utils/api';
import footerLogo from '../assets/images/footer.png';
import footerLogoBlack from '../assets/images/footer_black.png';

const MobileNav = ({ currentPage, setCurrentPage }) => {
  const [accountRole, setAccountRole] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    fetchAccountInfo();
  }, []);

  // Dark Mode Detection
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const activeElement = navRef.current?.querySelector(`[data-id="${currentPage}"]`);
    if (activeElement) {
      activeElement.scrollIntoView({
        behavior: 'smooth',
        inline: 'center', 
        block: 'nearest'
      });
    }
  }, [currentPage]);

  const fetchAccountInfo = async () => {
    try {
      const response = await accountAPI.getMe();
      if (response.data.success) {
        setAccountRole(response.data.data.role || '');
      }
    } catch (error) {
      console.error('Error fetching account info:', error);
    }
  };

  const menuItems = [
    { id: 'dashboard', label: 'Home', isLogo: true, color: 'from-blue-600 to-purple-600', roles: ['system', 'super_admin', 'reseller', 'user'] },
    { id: 'products', label: 'Products', icon: FiShoppingBag, color: 'from-emerald-500 to-teal-600', roles: ['system', 'super_admin', 'reseller' , 'user'] },
    { id: 'based-items', label: 'Based', icon: FiLayers, color: 'from-violet-500 to-purple-600', roles: ['system', 'super_admin'] },
    { id: 'custom-items', label: 'Packages', icon: FiGift, color: 'from-rose-500 to-pink-600', roles: ['system', 'super_admin', 'reseller' , 'user'] },
    { id: 'stock-management', label: 'Stock', icon: FiDatabase, color: 'from-amber-500 to-orange-600', roles: ['system', 'super_admin'] },
    { id: 'product-fields', label: 'Fields', icon: FiList, color: 'from-cyan-500 to-blue-500', roles: ['system'] },
    { id: 'history', label: 'History', icon: FiClock, color: 'from-teal-500 to-cyan-500', roles: ['system', 'super_admin', 'reseller', 'user'] },
    { id: 'guide', label: 'Guide', icon: FiBookOpen, color: 'from-sky-500 to-indigo-500', roles: ['system', 'super_admin', 'reseller', 'user'] },
    // { id: 'admin-tools', label: 'Admin Tool', icon: FiShield, color: 'from-rose-500 to-orange-500', roles: ['system', 'super_admin', 'reseller'] },
    { id: 'account', label: 'Profile', icon: FiUser, color: 'from-orange-500 to-red-600', roles: ['system', 'super_admin', 'reseller', 'user'] },
  ];

  const visibleItems = menuItems.filter(item => item.roles.includes(accountRole));

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 md:hidden">
      {/* Top Border Line Animation */}
      <motion.div 
        className="h-[2px] bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1 }}
      />
      
      <nav className="bg-white/90 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-100 dark:border-gray-800 pb-safe shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
        <div 
          ref={navRef}
          className="flex items-center overflow-x-auto no-scrollbar py-3 px-4 gap-2 snap-x snap-mandatory overscroll-x-contain"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x' }}
        >
          {visibleItems.map((item) => {
            const isActive = currentPage === item.id;
            const Icon = item.icon;
            
            return (
              <motion.button
                key={item.id}
                data-id={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`relative flex-shrink-0 flex flex-col items-center justify-center snap-center transition-all duration-300
                  ${isActive ? 'min-w-[85px] scale-110' : 'min-w-[70px] opacity-70'}
                `}
                whileTap={{ scale: 0.95 }}
              >
                {/* Active Highlight Background */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      layoutId="nav-pill"
                      className={`absolute inset-0 bg-gradient-to-br ${item.color} shadow-lg shadow-blue-500/20`}
                      style={{ borderRadius: '16px' }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ type: "spring", stiffness: 350, damping: 25 }}
                    />
                  )}
                </AnimatePresence>

                <div className="relative z-10 flex flex-col items-center py-2">
                  {item.isLogo ? (
                    /* Dashboard Logo with Special Animation */
                    <motion.div
                      animate={isActive 
                        ? { scale: 1.2, rotate: [0, 5, -5, 0] } 
                        : { scale: 1, filter: isDarkMode ? 'brightness(1)' : 'grayscale(1)' }
                      }
                      className={`relative w-7 h-7 mb-1 ${!isActive && 'opacity-60'}`}
                    >
                      <img 
                        src={isDarkMode || isActive ? footerLogo : footerLogoBlack}
                        alt="Logo" 
                        className="w-full h-full object-contain"
                      />
                      {/* Logo Pulse Effect */}
                      {isActive && (
                        <motion.div 
                          className="absolute inset-0 bg-white/30 rounded-full blur-md"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />
                      )}
                    </motion.div>
                  ) : (
                    /* Other Icons */
                    <motion.div
                      animate={isActive ? { y: -2 } : { y: 0 }}
                    >
                      <Icon className={`w-5 h-5 mb-1 transition-colors duration-200 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                    </motion.div>
                  )}
                  
                  <span className={`text-[10px] font-bold tracking-tight transition-colors duration-200 ${isActive ? 'text-white' : 'text-gray-400'}`}>
                    {item.label}
                  </span>
                </div>

                {/* Bottom Active Dot */}
                {isActive && (
                  <motion.div 
                    layoutId="active-dot"
                    className="absolute bottom-1 w-1 h-1 bg-white rounded-full"
                  />
                )}
              </motion.button>
            );
          })}
          
          {/* Spacer to allow scroll beyond last item */}
          <div className="flex-shrink-0 w-8 h-4" />
        </div>
      </nav>

      <style jsx>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .pb-safe { padding-bottom: env(safe-area-inset-bottom); }
        nav div { perspective: 1000px; }
      `}</style>
    </div>
  );
};

export default MobileNav;
